// @flow
/**
 * Renders the content of a slot on a page.
 * NOTE if the slot contains multiple components, it will wrap them in a <div>.
 */
import React, { Suspense } from 'react';
import ComponentLibrary from 'shared/widgets';
import type { $Slot, $Preset } from 'shared/templates/types';
import ErrorBoundary from 'shared/components/Error/ErrorBoundary';

type Props = {
  slots: $Slot[],
  id: string,
  widgetPresets: Array<$Preset>,
  path: string
};

const BasicSlot = ({
  slots,
  id,
  widgetPresets,
  path,
  ...rest
}: Props) => {
  const slot = slots && slots.find(s => s.slotId === id);

  if (!slot) {
    throw new Error(
      `Error: Tried to render slot with ID: ${id}, but it was not defined. Please
      check that you are passing all required slots to the template.`
    );
  }

  return (
    <div
      {...rest}
      className={slot.className}
      style={slot.style}
    >
      {slot && slot.components.map((component) => {
        if (React.isValidElement(component)) {
          // $FlowFixMe
          return component;
        }
        if (typeof component === 'string') return component;
        const { presetId, widgetId } = component;

        let { props } = component;
        const Component = ComponentLibrary[widgetId];

        if (presetId) {
          const preset = widgetPresets.find(p => p._id.toString() === presetId.toString());

          if (preset) {
            ({ props } = preset);
          } else {
            if (!preset) {
              throw new Error(
                `Warning: Tried to load component with preset ${presetId} but no preset
                was found. Ignoring the preset and render the component props instead.`
              );
            }
          }
        }

        props = {
          ...props,
          subcomponents: component.subcomponents,
          path,
          _id: component._id
        };

        // ErrorBoundary hides the component on error
        return (
          <ErrorBoundary key={component._id} widgetId={widgetId}>
            <Suspense fallback={<div />}>
              <Component
                {...props}
                isRootWidget // Widget is at root of the template (not hosted in another widget)
              />
            </Suspense>
          </ErrorBoundary>
        );
      })}
    </div>
  );
};

export default BasicSlot;
